import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/store/modules/user'
import { UserRole, PermissionAction, System, PermissionsAlias } from '@/router/config/roles'
import { RolePermissionConfig, type PermissionKey } from '@/router/config/permissions'
import { AgentRolePermissionConfig } from '@/router/config/permissions-agent'
import { RouteName } from '../router/config/route-names'

/**
 * 路由权限 Hook
 * 用于在组件中判断当前页面的操作权限
 *
 * @example
 * ```vue
 * <script setup>
 * import { useRoutePermission } from '@/hooks/useRoutePermission'
 * import { PermissionAction } from '@/router/config/roles'
 *
 * const { hasPermission, hasEditPermission } = useRoutePermission()
 *
 * // 方式1: 使用 hasPermission 函数
 * const canEdit = hasPermission(PermissionAction.EDIT)
 *
 * // 方式2: 使用预定义的计算属性
 * const canEdit = hasEditPermission.value
 * </script>
 * ```
 */
export function useRoutePermission() {
  const route = useRoute()
  const userStore = useUserStore()

  // 获取当前路由名称（直接使用路由的 name）
  const routeName = computed(() => {
    return route.name as string
  })

  // 获取当前用户角色
  const userRole = computed(() => {
    return userStore.roleId as UserRole | null
  })

  // 检查当前路由是否需要权限验证
  const requiresAuth = computed(() => {
    return route.meta?.requiresAuth !== false
  })

  // 根据用户系统选择权限配置
  const permissionConfig = computed(() => {
    if (userStore.userSystem === System.business) {
      return RolePermissionConfig
    } else if (userStore.userSystem === System.agent) {
      return AgentRolePermissionConfig
    }
    return {} as Record<UserRole, PermissionKey[]>
  })

  // 获取当前角色的所有权限
  const permissions = computed(() => {
    if (!userRole.value) return []
    return permissionConfig.value[userRole.value] || []
  })

  /**
   * 检查是否有特定操作权限
   * @param action 操作类型 'show' | 'edit' | 'download'
   * @returns 是否有权限
   */
  const hasPermission = (action: PermissionAction): boolean => {
    if (!routeName.value || !userRole.value) return false
    // 如果路由不需要权限验证，直接返回 true
    if (!requiresAuth.value) return true
    const permissionKey: PermissionKey = `${routeName.value as RouteName}:${action}`

    if (permissions.value == PermissionsAlias.hasAllPermission) { 
      return true;
    }
    let has = permissions.value.includes(permissionKey);
    return has
  }

  // 常用权限的计算属性
  const hasShowPermission = computed(() => hasPermission(PermissionAction.SHOW))
  const hasEditPermission = computed(() => hasPermission(PermissionAction.EDIT))
  const hasDownloadPermission = computed(() => hasPermission(PermissionAction.DOWNLOAD))

  return {
    routeName,
    userRole,
    requiresAuth,
    permissions,
    hasPermission,
    hasShowPermission,
    hasEditPermission,
    hasDownloadPermission
  }
}
